import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  useTheme,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import { ArrowBack, Send, Edit, CloudUpload } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { tokens } from "../../theme";
import { useDropzone } from "react-dropzone";

const apiURL = process.env.REACT_APP_API_URLDB;

const DenunciaForm = ({ onBack, onSubmit }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const mainBlue = "#273c7d";

  const [selectedFile, setSelectedFile] = useState(null);
  const [tiposDenuncia, setTiposDenuncia] = useState([]);

  useEffect(() => {
    const fetchTiposDenuncia = async () => {
      try {
        const response = await axios.get(`${apiURL}/tipos-denuncia`);
        setTiposDenuncia(response.data);
      } catch (error) {
        console.error("Error al obtener los tipos de denuncia:", error);
      }
    };

    fetchTiposDenuncia();
  }, []);

  const initialValues = {
    tipoDenuncia: "",
    descripcion: "",
  };

  const validationSchema = yup.object().shape({
    tipoDenuncia: yup.string().required("*Seleccione el tipo de denuncia*"),
    descripcion: yup.string().required("*Ingrese la descripción del reclamo*"),
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: {
      "image/*": [],
      "application/pdf": [],
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles[0]);
      }
    },
  });

  const handleSubmit = async (values) => {
    try {
      const response = await axios.get(`${apiURL}/idSolicitud`);
      const idSolicitud = response.data;

      if (!idSolicitud) {
        console.error("Error: idSolicitud no está definido.");
        return;
      }

      if (selectedFile) {
        const formData = new FormData();
        formData.append("idSolicitud", idSolicitud);
        formData.append("archivo", selectedFile);

        try {
          await axios.post(`${apiURL}/guardar-denuncia`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          window.location.reload();
        } catch (error) {
          console.error("Error al subir el archivo:", error);
          alert("Hubo un error al subir el archivo. Inténtalo de nuevo.");
          return;
        }
      }

      onSubmit({ ...values, idSolicitud });
      alert("¡La solicitud se creó correctamente!");
      window.location.reload();
    } catch (error) {
      console.error("Error al obtener el ID de solicitud:", error);
      alert("Hubo un error al obtener el ID de solicitud. Inténtalo de nuevo.");
    }
  };

  return (
    <Box>
      <Typography
        variant="h6"
        color={colors.grey[300]}
        mb={4}
        textAlign="center"
      >
        Detalle los motivos de su denuncia y adjunte documentación de respaldo
        si dispone de ella.
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px" gridTemplateColumns="repeat(2, 1fr)">
              {/* Tipo de Denuncia */}
              <FormControl
                fullWidth
                variant="outlined"
                error={!!touched.tipoDenuncia && !!errors.tipoDenuncia}
              >
                <InputLabel sx={{ color: mainBlue }}>
                  Tipo de denuncia
                </InputLabel>
                <Select
                  name="tipoDenuncia"
                  value={values.tipoDenuncia}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Tipo de denuncia"
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="" disabled>
                    <em>--Seleccione--</em>
                  </MenuItem>
                  {tiposDenuncia.map((tipo) => (
                    <MenuItem key={tipo.ID} value={tipo.ID}>
                      {tipo.Descripcion}
                    </MenuItem>
                  ))}
                </Select>
                {touched.tipoDenuncia && errors.tipoDenuncia && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ ml: 1, mt: 0.5 }}
                  >
                    {errors.tipoDenuncia}
                  </Typography>
                )}
              </FormControl>

              <Box>
                <TextField
                  fullWidth
                  multiline
                  color="secondary"
                  rows={4}
                  name="descripcion"
                  label="Descripción detallada del reclamo"
                  value={values.descripcion}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  error={!!touched.descripcion && !!errors.descripcion}
                  helperText={touched.descripcion && errors.descripcion}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Box>
            </Box>

            {/* Dropzone Area */}
            <Box
              mt={4}
              p={4}
              sx={{
                border: `2px dashed ${isDragActive ? mainBlue : "rgba(0,0,0,0.1)"}`,
                borderRadius: "16px",
                backgroundColor: isDragActive
                  ? "rgba(39, 60, 125, 0.05)"
                  : theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "#fcfdfe",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: mainBlue,
                  backgroundColor: "rgba(39, 60, 125, 0.02)",
                },
              }}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <CloudUpload
                sx={{ fontSize: 48, color: mainBlue, mb: 1.5, opacity: 0.8 }}
              />
              {isDragActive ? (
                <Typography variant="h6" color={mainBlue}>
                  ¡Suelte el archivo aquí!
                </Typography>
              ) : (
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {selectedFile
                      ? "Archivo seleccionado con éxito"
                      : "Adjuntar documentos de respaldo"}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[300]}>
                    {selectedFile
                      ? selectedFile.name
                      : "(PDF o Imágenes permitidas)"}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Action Buttons */}
            <Box
              mt={5}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button
                variant="outlined"
                onClick={onBack}
                startIcon={<ArrowBack />}
                sx={{
                  color: colors.grey[300],
                  borderColor: colors.grey[500],
                  borderRadius: "10px",
                  px: 4,
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": {
                    borderColor: mainBlue,
                    color: mainBlue,
                  },
                }}
              >
                Anterior
              </Button>
              <Button
                type="submit"
                variant="contained"
                endIcon={<Send />}
                sx={{
                  backgroundColor: mainBlue,
                  color: "white",
                  borderRadius: "10px",
                  px: 8,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#1c2a4a",
                    transform: "translateY(-3px)",
                    boxShadow: "0px 8px 20px rgba(0,0,0,0.2)",
                  },
                }}
              >
                Finalizar y Enviar
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default DenunciaForm;
